import React, { useMemo, useState } from "react";
import { mapToOptions } from "../../../lib/functions";
import {
  useGetAllPlateLetters,
  useGetAllPlateSource,
} from "../../../hooks/common/query";
import Accordion from "../../../components/accordian";
import { useTranslation } from "react-i18next";
import { Vehicle, WarehousePermitWorker } from "../../../constants/common";
import { Button } from "../../../components/button";
import { Plus } from "lucide-react";
import PermitVehiclesTable from "../../../components/table/PermitVehiclesTable";
import VehicleForm from "./VehicleForm";

interface VehicleInformationProps {
  addedVehicles: Vehicle[];
  setAddedVehicles: React.Dispatch<React.SetStateAction<Vehicle[]>>;
  addedWorkers: WarehousePermitWorker[];
  quantityOfItems: number;
  isFieldsDisabled: boolean;
}

const VehicleInformation = ({
  addedVehicles,
  setAddedVehicles,
  isFieldsDisabled = false,
  addedWorkers,
}: VehicleInformationProps) => {
  const [selectedKey, setSelectedKey] = useState<string>("1");

  const { t } = useTranslation();

  const { data: allPlateSource } = useGetAllPlateSource();

  const plateSource = useMemo(
    () =>
      mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", allPlateSource),
    [allPlateSource]
  );

  const { data: allPlateLetters } = useGetAllPlateLetters();

  const plateLetters = useMemo(
    () =>
      mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", allPlateLetters),
    [allPlateLetters]
  );

  const driversNameOptions = addedWorkers.map((worker) => ({
    labelEn: worker.name,
    labelAr: worker.name,
    value: worker.name,
  }));

  const [isAddVehicleFormVisible, setIsAddVehicleFormVisible] =
    useState<boolean>(true);

  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);

  const handleFormReset = () => {
    setIsAddVehicleFormVisible(false);
    setEditingVehicle(null);
  };

  return (
    <div className="bg-white p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordion
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 items-end -mt-2">
            {t("4")}. {t("vehicleInformation")}
          </h1>
        }
      >
        {!isFieldsDisabled && (
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={() => {
                setIsAddVehicleFormVisible(false);
                setEditingVehicle(null);
                setTimeout(() => {
                  setIsAddVehicleFormVisible(true);
                }, 0);
              }}
              Icon={<Plus size={20} />}
            >
              {t("addVehicle")}
            </Button>
          </div>
        )}

        <PermitVehiclesTable
          addedVehicles={addedVehicles}
          setAddedVehicles={setAddedVehicles}
          setEditingVehicle={setEditingVehicle}
          setIsAddVehicleFormVisible={setIsAddVehicleFormVisible}
          onFormReset={handleFormReset}
          plateSource={plateSource}
          plateLetters={plateLetters}
          driversNameOptions={driversNameOptions}
          isFieldsDisabled={isFieldsDisabled}
        />

        {isAddVehicleFormVisible && (
          <VehicleForm
            isFieldsDisabled={isFieldsDisabled}
            setIsAddVehicleFormVisible={setIsAddVehicleFormVisible}
            setAddedVehicles={setAddedVehicles}
            editingVehicle={editingVehicle}
            setEditingVehicle={setEditingVehicle}
            plateSource={plateSource}
            plateLetters={plateLetters}
            driversNameOptions={driversNameOptions}
            addedWorkers={addedWorkers}
          />
        )}
      </Accordion>
    </div>
  );
};

export default React.memo(VehicleInformation);
